import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { getGeneralServiceMaster, getLabTestMaster, addSales } from "../../SalesService";
import { buildInvoicePayloadWithTax } from "../utils/taxCalculations";
import { getDefaultCurrency } from "../utils/salesCalculations";
import buildSalesFormData from "../utils/commonUtils";
import { buildBillingInvoiceData } from "../utils/billingInvoiceUtils";
import { buildOptionalSaleMeta, isPlaceholderDoctorName, sanitizeDoctorName } from "../utils/doctorFormUtils";
import { resolveDoctorIdForSale } from "../utils/doctorResolution";
import { formatPrescriptionDateForPayload } from "../utils/dateUtils";
import {
	buildBillingSummary,
	buildBillingContextFields,
	buildSectionLineBillingFieldsMap,
	normalizeBillingLineAmount,
	convertLineAmount,
	buildPharmacyItemBillingFieldsMap,
	resolveTreatmentBillableServiceItems,
	resolveTreatmentCollectPaymentInvestigations,
	buildOverallPharmacyTotals,
	buildTreatmentBillingSummary,
	buildTreatmentBillingPayloadExtras,
	getTreatmentPackagePrice,
	isTreatmentPackageInvoice,
	buildPharmacyItemsFromCart,
	buildPharmacyTotalsFromBilling,
	getPriorPharmacyItemsFromPrescription,
	mergePharmacyTotals,
	appendPharmacyItemsForBilling,
	calculateServicesBillingTotals,
	calculateInvestigationBillingTotals,
	createEmptyInvestigationRow,
	createEmptySectionTaxConfig,
	createEmptyServiceRow,
	enrichInvestigationsWithMaster,
	enrichServiceItemsWithMaster,
	extractSuggestedInvestigations,
	generalServiceFromMaster,
	getBillingBaseCurrency,
	getPriorBillingPaymentOffsets,
	getEffectiveSectionTaxConfig,
	getPrescriptionBillingFlags,
	getSectionDiscountFromSummary,
	getSectionTaxConfig,
	getTaxConfigFromBillingSummary,
	getInvestigationUnitPrice,
	hasPatientAccountBilling,
	initializePatientAccountBillingItems,
	investigationFromMaster,
	normalizeCurrencyCode,
	parseGeneralServiceMasterResponse,
	parseLabMasterResponse,
	recalculateInvestigationLine,
	recalculateServiceLine,
	getServiceUnitPrice,
	resolveActivePrescription,
	syncGstTaxConfig,
	filterNewBillingInvestigationsForPayload,
	filterNewBillingGeneralServicesForPayload,
	filterNewBillingPharmacyItemsForPayload,
	withBillingPaymentAmountFields,
} from "../utils/billingUtils";
import { toMoneyNumber } from "../../../../utils/financeEngine";
import {
	buildTreatmentPackageDetails,
	buildTreatmentPackagePaymentHistory,
} from "../../../PatientAccounts/utils/patientAccountUtils";

const INITIAL_PAYMENT = (pharmacyPOS) => ({
	currency: getDefaultCurrency(pharmacyPOS),
	payment_method: "cash",
	amount_received: "",
});

const convertBillingAmount = (amount, fromCurrency, toCurrency, convertCurrency) => {
	if (!amount || fromCurrency === toCurrency) return toMoneyNumber(amount);
	if (typeof convertCurrency !== "function") return toMoneyNumber(amount);
	return toMoneyNumber(convertCurrency(amount, fromCurrency, toCurrency));
};

const PAYMENT_TOLERANCE = 0.01;

const isFullyPaidAmount = (received, grandTotal) => {
	const paid = Number(received) || 0;
	const total = Number(grandTotal) || 0;
	return paid > 0 && paid >= total - PAYMENT_TOLERANCE;
};

const resolvePaymentStatus = (received, grandTotal, markAsPaid = false) => {
	if (markAsPaid || isFullyPaidAmount(received, grandTotal)) return "paid";
	if ((Number(received) || 0) <= 0) return "unpaid";
	return "partial_paid";
};

const resolveBillingSalePaymentFlags = ({ markAsPaid, status, settledAsPaid }) => {
	const normalized = String(status || "").toLowerCase();
	const isPaidStatus = normalized === "paid";
	const isPartialStatus = ["partial_paid", "partially_paid", "partial"].includes(normalized);
	const isSettledPayment = settledAsPaid === true || markAsPaid === true;

	// Collect & Mark As Paid (incl. zero / underpaid) → paid + settled, never partial
	if (isSettledPayment) {
		return {
			paidType: "full",
			isPartialPaid: false,
			isPaid: true,
			isSettled: true,
		};
	}

	if (isPaidStatus) {
		return {
			paidType: "full",
			isPartialPaid: false,
			isPaid: true,
			isSettled: false,
		};
	}

	if (isPartialStatus) {
		return {
			paidType: "partial",
			isPartialPaid: true,
			isPaid: false,
			isSettled: false,
		};
	}

	return {
		paidType: "partial",
		isPartialPaid: false,
		isPaid: false,
		isSettled: false,
	};
};

export const useBillingManager = ({
	t,
	pharmacyPOS,
	prescriptionDetails,
	selectedPrescription,
	recentPrescriptions,
	cartPrescriptionId,
	selectedPatient,
	cart,
	totals,
	itemTaxes,
	discountType,
	discountValue,
	transportCost,
	customerType,
	posTaxType,
	pharmacyStateCode,
	patientStateCode,
	convertCurrency,
	refreshRates,
	currency,
	allowedCurrencies = [],
	buildSalePaymentData,
	setLoading,
	prescriptionFile,
	getCurrencySymbol,
	doctorId,
	doctorForm,
	setDoctorId,
	hydrateDoctorFromPrescription,
	pharmacyCollectsPayment = false,
}) => {
	const [showBillingModal, setShowBillingModal] = useState(false);
	const [billingLoading, setBillingLoading] = useState(false);
	const billingInitIdRef = useRef(0);
	const [billingSubmitting, setBillingSubmitting] = useState(false);
	const [showBillingInvoice, setShowBillingInvoice] = useState(false);
	const [billingInvoiceData, setBillingInvoiceData] = useState(null);
	const [showBackendInvoice, setShowBackendInvoice] = useState(false);
	const [backendInvoiceUrl, setBackendInvoiceUrl] = useState("");
	const [backendInvoiceNo, setBackendInvoiceNo] = useState("");
	const [activePrescription, setActivePrescription] = useState(null);
	const [serviceItems, setServiceItems] = useState([]);
	const [servicesDiscountType, setServicesDiscountType] = useState("Percentage");
	const [servicesDiscountValue, setServicesDiscountValue] = useState(0);
	const [servicesTaxConfig, setServicesTaxConfig] = useState(() =>
		createEmptySectionTaxConfig(posTaxType)
	);
	const [investigationsDiscountType, setInvestigationsDiscountType] = useState("Percentage");
	const [investigationsDiscountValue, setInvestigationsDiscountValue] = useState(0);
	const [investigationsTaxConfig, setInvestigationsTaxConfig] = useState(() =>
		createEmptySectionTaxConfig(posTaxType)
	);
	const [investigationItems, setInvestigationItems] = useState([]);
	const [labMasterList, setLabMasterList] = useState([]);
	const [generalMasterList, setGeneralMasterList] = useState([]);
	const [payment, setPayment] = useState(INITIAL_PAYMENT(pharmacyPOS));
	const [errors, setErrors] = useState({});
	const [submitted, setSubmitted] = useState(false);
	const [sectionOpen, setSectionOpen] = useState({
		package: true,
		sessions: true,
		services: true,
		investigations: true,
		medicines: true,
		history: true,
	});
	const paymentPrefilledRef = useRef(false);
	const prevGrandTotalRef = useRef(null);

	const defaultCurrency = currency || getDefaultCurrency(pharmacyPOS);

	const isSameState = useMemo(
		() =>
			!patientStateCode ||
			!pharmacyStateCode ||
			pharmacyStateCode === patientStateCode,
		[patientStateCode, pharmacyStateCode]
	);

	const billingFlags = useMemo(
		() => getPrescriptionBillingFlags(activePrescription),
		[activePrescription]
	);

	const isTreatmentPackage = useMemo(
		() => isTreatmentPackageInvoice(activePrescription),
		[activePrescription]
	);

	const treatmentDetails = useMemo(
		() => (isTreatmentPackage ? buildTreatmentPackageDetails(activePrescription) : null),
		[activePrescription, isTreatmentPackage]
	);

	const treatmentPaymentHistory = useMemo(
		() =>
			isTreatmentPackage
				? buildTreatmentPackagePaymentHistory(activePrescription, getCurrencySymbol)
				: [],
		[activePrescription, getCurrencySymbol, isTreatmentPackage]
	);

	const packagePrice = useMemo(
		() => (isTreatmentPackage ? getTreatmentPackagePrice(activePrescription) : 0),
		[activePrescription, isTreatmentPackage]
	);

	// EMR investigation[] labs are suggestions only — never auto-selected / billable.
	const suggestedInvestigations = useMemo(
		() => extractSuggestedInvestigations(activePrescription?.investigation),
		[activePrescription?.investigation]
	);

	const billingBaseCurrency = useMemo(
		() => getBillingBaseCurrency(activePrescription, defaultCurrency),
		[activePrescription, defaultCurrency]
	);

	const servicesBillingTotals = useMemo(
		() =>
			calculateServicesBillingTotals({
				serviceItems,
				discountType: servicesDiscountType,
				discountValue: servicesDiscountValue,
				taxConfig: getEffectiveSectionTaxConfig(
					servicesTaxConfig,
					billingFlags.showServicesTax,
					posTaxType
				),
				isSameState,
				targetCurrency: billingBaseCurrency,
				convertCurrency,
				pharmacyCurrency: defaultCurrency,
				allowedCurrencies,
			}),
		[
			allowedCurrencies,
			billingBaseCurrency,
			convertCurrency,
			defaultCurrency,
			isSameState,
			serviceItems,
			servicesDiscountType,
			servicesDiscountValue,
			servicesTaxConfig,
			billingFlags.showServicesTax,
			posTaxType,
		]
	);

	const investigationsBillingTotals = useMemo(
		() =>
			calculateInvestigationBillingTotals({
				investigationItems,
				discountType: investigationsDiscountType,
				discountValue: investigationsDiscountValue,
				taxConfig: getEffectiveSectionTaxConfig(
					investigationsTaxConfig,
					isTreatmentPackage || billingFlags.showLabTax,
					posTaxType
				),
				isSameState,
				targetCurrency: billingBaseCurrency,
				convertCurrency,
				pharmacyCurrency: defaultCurrency,
				allowedCurrencies,
			}),
		[
			allowedCurrencies,
			billingBaseCurrency,
			convertCurrency,
			defaultCurrency,
			investigationItems,
			investigationsDiscountType,
			investigationsDiscountValue,
			investigationsTaxConfig,
			billingFlags.showLabTax,
			isTreatmentPackage,
			isSameState,
			posTaxType,
		]
	);

	const priorPharmacyItems = useMemo(
		() => getPriorPharmacyItemsFromPrescription(activePrescription),
		[activePrescription]
	);

	const cartPharmacyItems = useMemo(() => buildPharmacyItemsFromCart(cart), [cart]);

	const pharmacyItems = useMemo(
		() => appendPharmacyItemsForBilling(priorPharmacyItems, cartPharmacyItems),
		[priorPharmacyItems, cartPharmacyItems]
	);

	const priorPharmacyTotals = useMemo(
		() => buildPharmacyTotalsFromBilling(activePrescription?.billing),
		[activePrescription]
	);

	const combinedPharmacyTotals = useMemo(() => {
		const overall = buildOverallPharmacyTotals({
			priorItems: priorPharmacyItems,
			cartTotals: totals,
			discountType,
			discountValue,
			isSameState,
			posTaxType,
			baseCurrency: billingBaseCurrency,
			pharmacyCurrency: defaultCurrency,
			convertCurrency,
			allowedCurrencies,
		});
		// Prefer line-aggregated totals when prior medicines exist so tax/discount
		// cover previous + new medicines in Billing Summary.
		if ((priorPharmacyItems || []).length > 0) {
			return overall;
		}
		return mergePharmacyTotals(priorPharmacyTotals, totals);
	}, [
		allowedCurrencies,
		billingBaseCurrency,
		convertCurrency,
		defaultCurrency,
		discountType,
		discountValue,
		isSameState,
		posTaxType,
		priorPharmacyItems,
		priorPharmacyTotals,
		totals,
	]);

	const billingSummary = useMemo(() => {
		const showLab = isTreatmentPackage || billingFlags.showLabSection;
		const pharmacyTotalsAlreadyInBase = combinedPharmacyTotals?.inBaseCurrency === true;
		const baseSummary = buildBillingSummary({
			serviceItems,
			servicesBilling: servicesBillingTotals,
			investigationItems: showLab ? investigationItems : [],
			investigationsBilling: showLab ? investigationsBillingTotals : null,
			pharmacyItems,
			pharmacyTotals: combinedPharmacyTotals,
			pharmacyDiscountType: discountType,
			pharmacyDiscountValue: discountValue,
			targetCurrency: billingBaseCurrency,
			convertCurrency,
			// When overall totals are already in base, skip re-converting as pharmacy currency.
			pharmacyCurrency: pharmacyTotalsAlreadyInBase
				? billingBaseCurrency
				: defaultCurrency,
			allowedCurrencies,
		});

		if (!isTreatmentPackage) return baseSummary;

		return buildTreatmentBillingSummary({
			baseSummary,
			prescription: activePrescription,
			serviceItems,
			packagePrice,
			targetCurrency: billingBaseCurrency,
			convertCurrency,
			pharmacyCurrency: pharmacyTotalsAlreadyInBase
				? billingBaseCurrency
				: defaultCurrency,
			allowedCurrencies,
		});
	}, [
		activePrescription,
		allowedCurrencies,
		billingBaseCurrency,
		combinedPharmacyTotals,
		convertCurrency,
		defaultCurrency,
		discountType,
		discountValue,
		investigationItems,
		investigationsBillingTotals,
		billingFlags.showLabSection,
		isTreatmentPackage,
		packagePrice,
		pharmacyItems,
		serviceItems,
		servicesBillingTotals,
	]);

	const priorPayment = useMemo(
		() =>
			getPriorBillingPaymentOffsets({
				prescription: activePrescription,
				grandTotal: billingSummary.grand_total,
				isTreatmentPackage,
				treatmentDetails,
			}),
		[
			activePrescription,
			billingSummary.grand_total,
			isTreatmentPackage,
			treatmentDetails,
		]
	);

	const fetchLabMasterList = useCallback(
		async (search = "") => {
			try {
				const response = await getLabTestMaster({
					clinic_id: pharmacyPOS?.hospital_id,
					search: search || "",
				});
				return parseLabMasterResponse(response);
			} catch (error) {
				console.error("Error loading lab tests:", error);
				return [];
			}
		},
		[pharmacyPOS?.hospital_id]
	);

	const fetchGeneralMasterList = useCallback(
		async (search = "") => {
			try {
				const response = await getGeneralServiceMaster({
					clinic_id: pharmacyPOS?.hospital_id,
					search: search || "",
				});
				return parseGeneralServiceMasterResponse(response);
			} catch (error) {
				console.error("Error loading general services:", error);
				return [];
			}
		},
		[pharmacyPOS?.hospital_id]
	);

	const initializeBillingState = useCallback(
		async (prescription, { isCancelled } = {}) => {
			const source = resolveActivePrescription(prescription);
			if (!source) return null;
			
			const [labMaster, generalMaster] = await Promise.all([
				fetchLabMasterList(""),
				fetchGeneralMasterList(""),
			]);
			if (isCancelled?.()) return null;

			setLabMasterList(labMaster);
			setGeneralMasterList(generalMaster);
			setActivePrescription(source);

			const treatmentPackage = isTreatmentPackageInvoice(source);
			const hasSavedBilling = hasPatientAccountBilling(source);
			const billing = source?.billing || {};

			// Identical source resolution as Collect Payment (useCollectPaymentBilling).
			const { serviceItems: rawServices, investigationItems: rawInvestigations } =
				treatmentPackage
					? {
							serviceItems: resolveTreatmentBillableServiceItems(
								source,
								defaultCurrency
							),
							investigationItems: resolveTreatmentCollectPaymentInvestigations(
								source,
								defaultCurrency,
								allowedCurrencies
							),
						}
					: initializePatientAccountBillingItems(
							source,
							defaultCurrency,
							allowedCurrencies
						);

			const initialServices = enrichServiceItemsWithMaster(
				rawServices,
				generalMaster,
				defaultCurrency,
				allowedCurrencies
			);
			const initialInvestigations = enrichInvestigationsWithMaster(
				rawInvestigations,
				labMaster,
				defaultCurrency,
				allowedCurrencies
			);

			setServiceItems(initialServices);
			setInvestigationItems(initialInvestigations);

			if (
				typeof hydrateDoctorFromPrescription === "function" &&
				source?.doctor?.name?.trim() &&
				!isPlaceholderDoctorName(source.doctor.name)
			) {
				await hydrateDoctorFromPrescription(source.doctor);
				if (isCancelled?.()) return null;
			}

			// Same discount/tax restore as Collect Payment
			const summary = billing.summary || {};
			const servicesDiscount = getSectionDiscountFromSummary(summary, "services");
			const investigationsDiscount = getSectionDiscountFromSummary(
				summary,
				"investigation"
			);
			setServicesDiscountType(servicesDiscount.discountType);
			setServicesDiscountValue(servicesDiscount.discountValue);
			setInvestigationsDiscountType(investigationsDiscount.discountType);
			setInvestigationsDiscountValue(investigationsDiscount.discountValue);

			const nextServicesTax = hasSavedBilling
				? getTaxConfigFromBillingSummary(summary, "services", posTaxType)
				: getSectionTaxConfig(source, posTaxType);
			const nextInvestigationsTax = hasSavedBilling
				? getTaxConfigFromBillingSummary(summary, "investigation", posTaxType)
				: createEmptySectionTaxConfig(posTaxType);

			setServicesTaxConfig(nextServicesTax);
			setInvestigationsTaxConfig(nextInvestigationsTax);

			const baseCurrency = getBillingBaseCurrency(source, defaultCurrency);
			setPayment({
				currency: normalizeCurrencyCode(
					baseCurrency,
					defaultCurrency,
					allowedCurrencies
				),
				payment_method: "cash",
				amount_received: "",
			});
			setErrors({});
			setSubmitted(false);
			return source;
		},
		[
			allowedCurrencies,
			defaultCurrency,
			fetchGeneralMasterList,
			fetchLabMasterList,
			hydrateDoctorFromPrescription,
			posTaxType,
		]
	);

	const openBillingModal = useCallback(
		async ({ prescription } = {}) => {
			const cartPrescription = cartPrescriptionId
				? recentPrescriptions?.find((item) => item._id === cartPrescriptionId)
				: null;

			const source = resolveActivePrescription(
				prescription,
				prescriptionDetails,
				cartPrescription,
				selectedPrescription,
				recentPrescriptions?.[0]
			);

			// if (!source) {
			// 	toast.info(t("Please select a prescription for this patient."));
			// 	return false;
			// }

			const initId = ++billingInitIdRef.current;
			const isCancelled = () => initId !== billingInitIdRef.current;

			setShowBillingModal(true);
			setBillingLoading(true);
			paymentPrefilledRef.current = false;
			prevGrandTotalRef.current = null;
			try {
				await initializeBillingState(source, { isCancelled });
				if (isCancelled()) return false;
				return true;
			} catch (error) {
				if (isCancelled()) return false;
				console.error("Error initializing billing:", error);
				toast.error(t("Failed to load billing details. Please try again."));
				setShowBillingModal(false);
				return false;
			} finally {
				if (!isCancelled()) {
					setBillingLoading(false);
				}
			}
		},
		[
			cartPrescriptionId,
			initializeBillingState,
			prescriptionDetails,
			recentPrescriptions,
			selectedPrescription,
			t,
		]
	);

	useEffect(() => {
		if (!showBillingModal || billingLoading) return;
		const grandTotalBase = billingSummary.grand_total;
		const paymentCurrency = payment.currency || billingBaseCurrency;
		const grandTotalInPayment = convertBillingAmount(
			grandTotalBase,
			billingBaseCurrency,
			paymentCurrency,
			convertCurrency
		);
		const targetInPayment = priorPayment.hasPriorPayment
			? convertBillingAmount(
					priorPayment.balanceDueBase,
					billingBaseCurrency,
					paymentCurrency,
					convertCurrency
				)
			: grandTotalInPayment;

		setPayment((prev) => {
			const received = toMoneyNumber(prev.amount_received);
			const prevTarget = prevGrandTotalRef.current;

			if (!paymentPrefilledRef.current && targetInPayment > 0) {
				paymentPrefilledRef.current = true;
				prevGrandTotalRef.current = targetInPayment;
				return {
					...prev,
					amount_received: String(toMoneyNumber(targetInPayment)),
				};
			}

			if (
				paymentPrefilledRef.current &&
				prevTarget !== null &&
				received === toMoneyNumber(prevTarget) &&
				targetInPayment !== prevTarget
			) {
				prevGrandTotalRef.current = targetInPayment;
				return {
					...prev,
					amount_received: String(toMoneyNumber(targetInPayment)),
				};
			}

			prevGrandTotalRef.current = targetInPayment;
			return prev;
		});
	}, [
		billingBaseCurrency,
		billingLoading,
		billingSummary.grand_total,
		convertCurrency,
		payment.currency,
		priorPayment.balanceDueBase,
		priorPayment.hasPriorPayment,
		showBillingModal,
	]);

	const closeBillingModal = useCallback(() => {
		billingInitIdRef.current += 1;
		setShowBillingModal(false);
		setBillingLoading(false);
		setErrors({});
		setSubmitted(false);
	}, []);

	const closeBillingInvoice = useCallback(() => {
		setShowBillingInvoice(false);
		setBillingInvoiceData(null);
	}, []);

	const closeBackendInvoice = useCallback(() => {
		setShowBackendInvoice(false);
		setBackendInvoiceUrl("");
		setBackendInvoiceNo("");
	}, []);

	const resetBillingState = useCallback(() => {
		setActivePrescription(null);
		setServiceItems([]);
		setServicesDiscountType("Percentage");
		setServicesDiscountValue(0);
		setServicesTaxConfig(createEmptySectionTaxConfig(posTaxType));
		setInvestigationsDiscountType("Percentage");
		setInvestigationsDiscountValue(0);
		setInvestigationsTaxConfig(createEmptySectionTaxConfig(posTaxType));
		setInvestigationItems([]);
		setLabMasterList([]);
		setGeneralMasterList([]);
		setPayment(INITIAL_PAYMENT(pharmacyPOS));
		setErrors({});
		setSubmitted(false);
		setBillingLoading(false);
		setShowBillingModal(false);
		setShowBillingInvoice(false);
		setBillingInvoiceData(null);
		setShowBackendInvoice(false);
		setBackendInvoiceUrl("");
		setBackendInvoiceNo("");
	}, [pharmacyPOS, posTaxType]);

	const toggleSection = useCallback((key) => {
		setSectionOpen((prev) => ({ ...prev, [key]: !prev[key] }));
	}, []);

	const updateInvestigationAmount = useCallback((id, amount) => {
		setInvestigationItems((prev) =>
			prev.map((item) => {
				if (item.id !== id) return item;
				const quantity = Math.max(1, Number(item.quantity) || 1);
				const lineAmount = toMoneyNumber(amount);
				return {
					...item,
					amount: lineAmount,
					unit_price: toMoneyNumber(lineAmount / quantity),
				};
			})
		);
	}, []);

	const updateInvestigationQuantity = useCallback((id, quantity) => {
		setInvestigationItems((prev) =>
			prev.map((item) =>
				item.id === id
					? { ...item, ...recalculateInvestigationLine(item, quantity) }
					: item
			)
		);
	}, []);

	const updateServiceAmount = useCallback((id, amount) => {
		setServiceItems((prev) =>
			prev.map((item) => {
				if (item.id !== id) return item;
				const quantity = Math.max(1, Number(item.quantity) || 1);
				const lineAmount = toMoneyNumber(amount);
				return {
					...item,
					amount: lineAmount,
					unit_price: toMoneyNumber(lineAmount / quantity),
				};
			})
		);
	}, []);

	const updateServiceQuantity = useCallback((id, quantity) => {
		setServiceItems((prev) =>
			prev.map((item) =>
				item.id === id ? { ...item, ...recalculateServiceLine(item, quantity) } : item
			)
		);
	}, []);

	const removeInvestigation = useCallback((id) => {
		setInvestigationItems((prev) => prev.filter((item) => item.id !== id));
	}, []);

	const updateServicesDiscount = useCallback((patch) => {
		if (patch.discountType !== undefined) {
			setServicesDiscountType(patch.discountType);
		}
		if (patch.discountValue !== undefined) {
			setServicesDiscountValue(toMoneyNumber(patch.discountValue));
		}
	}, []);

	const clampTaxPercent = (value) => {
		const num = toMoneyNumber(value);
		if (num < 0) return 0;
		if (num > 100) return 100;
		return num;
	};

	const updateServicesTaxConfig = useCallback((patch) => {
		setServicesTaxConfig((prev) => {
			let next = { ...prev, ...patch };
			if (patch.taxPercentage !== undefined) {
				next.taxPercentage = clampTaxPercent(patch.taxPercentage);
			}
			if (patch.taxType !== undefined) {
				next.taxType = String(patch.taxType).toLowerCase();
			}
			if (next.taxType !== "vat") {
				next = syncGstTaxConfig(next);
			}
			return next;
		});
	}, []);

	const updateInvestigationsDiscount = useCallback((patch) => {
		if (patch.discountType !== undefined) {
			setInvestigationsDiscountType(patch.discountType);
		}
		if (patch.discountValue !== undefined) {
			setInvestigationsDiscountValue(toMoneyNumber(patch.discountValue));
		}
	}, []);

	const updateInvestigationsTaxConfig = useCallback((patch) => {
		setInvestigationsTaxConfig((prev) => {
			let next = { ...prev, ...patch };
			if (patch.taxPercentage !== undefined) {
				next.taxPercentage = clampTaxPercent(patch.taxPercentage);
			}
			if (patch.taxType !== undefined) {
				next.taxType = String(patch.taxType).toLowerCase();
			}
			if (next.taxType !== "vat") {
				next = syncGstTaxConfig(next);
			}
			return next;
		});
	}, []);

	const removeServiceItem = useCallback((id) => {
		setServiceItems((prev) => {
			const target = prev.find((item) => item.id === id);
			if (
				!target ||
				target.isConsultation ||
				(target.isPrescribed && !target.isNewRow)
			) {
				return prev;
			}
			return prev.filter((item) => item.id !== id);
		});
	}, []);

	const addServiceRow = useCallback(() => {
		setServiceItems((prev) => [...prev, createEmptyServiceRow(defaultCurrency)]);
	}, [defaultCurrency]);

	const addInvestigationRow = useCallback(() => {
		setInvestigationItems((prev) => [...prev, createEmptyInvestigationRow(defaultCurrency)]);
	}, [defaultCurrency]);

	const selectServiceForRow = useCallback(
		(rowId, serviceId) => {
			if (!serviceId) {
				setServiceItems((prev) =>
					prev.map((item) =>
						item.id === rowId
							? {
									...item,
									service_id: "",
									service_name: "",
									quantity: 1,
									unit_price: 0,
									amount: 0,
									isNewRow: true,
								}
							: item
					)
				);
				return;
			}
			const master = generalMasterList.find(
				(item) => String(item.service_id || item._id) === String(serviceId)
			);
			if (!master) return;

			const candidate = generalServiceFromMaster(
				master,
				defaultCurrency,
				allowedCurrencies
			);

			setServiceItems((prev) => {
				const duplicate = prev.some(
					(item) =>
						item.id !== rowId &&
						String(item.service_id) === String(candidate.service_id)
				);
				if (duplicate) {
					toast.info(t("Service already added."));
					return prev;
				}
				return prev.map((item) =>
					item.id === rowId
						? {
								...item,
								...candidate,
								id: item.id,
								isNewRow: true,
								isPrescribed: false,
								source: "added",
							}
						: item
				);
			});
		},
		[allowedCurrencies, defaultCurrency, generalMasterList, t]
	);

	const selectInvestigationForRow = useCallback(
		(rowId, testId) => {
			if (!testId) {
				setInvestigationItems((prev) =>
					prev.map((item) =>
						item.id === rowId
							? {
									...item,
									test_id: "",
									test_name: "",
									quantity: 1,
									unit_price: 0,
									amount: 0,
									isNewRow: true,
								}
							: item
					)
				);
				return;
			}
			const master = labMasterList.find(
				(item) => String(item.investigation_id || item._id) === String(testId)
			);
			if (!master) return;

			const candidate = investigationFromMaster(
				master,
				defaultCurrency,
				allowedCurrencies
			);

			setInvestigationItems((prev) =>
				prev.map((item) =>
					item.id === rowId
						? {
								...item,
								...candidate,
								id: item.id,
								isNewRow: true,
								isPrescribed: false,
								source: "added",
							}
						: item
				)
			);
		},
		[allowedCurrencies, defaultCurrency, labMasterList]
	);

	const updatePayment = useCallback(
		async (patch) => {
			if (patch.currency) {
				await refreshRates?.();
			}

			setPayment((prev) => {
				const next = { ...prev, ...patch };

				if (patch.currency && patch.currency !== prev.currency) {
					const currentAmount = Number(prev.amount_received) || 0;
					if (currentAmount > 0) {
						next.amount_received = String(
							convertBillingAmount(
								currentAmount,
								prev.currency,
								patch.currency,
								convertCurrency
							)
						);
					}
				}

				return next;
			});

			setErrors((prev) => {
				const next = { ...prev };
				Object.keys(patch).forEach((key) => {
					delete next[key];
				});
				return next;
			});
		},
		[convertCurrency, refreshRates]
	);

	const paymentBalance = useMemo(() => {
		const grandTotalBase = billingSummary.grand_total;
		const paymentCurrency = payment.currency || billingBaseCurrency;
		const grandTotalInPayment = convertBillingAmount(
			grandTotalBase,
			billingBaseCurrency,
			paymentCurrency,
			convertCurrency
		);
		const paidSoFarBase = priorPayment.paidSoFarBase;
		const paidSoFarInPayment = convertBillingAmount(
			paidSoFarBase,
			billingBaseCurrency,
			paymentCurrency,
			convertCurrency
		);
		const amountDueBase = priorPayment.balanceDueBase;
		const amountDueInPayment = convertBillingAmount(
			amountDueBase,
			billingBaseCurrency,
			paymentCurrency,
			convertCurrency
		);
		const received = Number(payment.amount_received) || 0;
		const pending = Math.max(0, amountDueInPayment - received);
		const change = Math.max(0, received - amountDueInPayment);
		const status = resolvePaymentStatus(received, amountDueInPayment);
		const statusLabel =
			status === "paid" ? "Paid" : status === "partial_paid" ? "Partial Paid" : "Unpaid";

		const receivedBase = convertBillingAmount(
			received,
			paymentCurrency,
			billingBaseCurrency,
			convertCurrency
		);
		const pendingBase = Math.max(0, amountDueBase - receivedBase);
		const changeBase = Math.max(0, receivedBase - amountDueBase);

		return {
			grandTotal: grandTotalInPayment,
			grandTotalBase,
			paidSoFar: paidSoFarInPayment,
			paidSoFarBase,
			amountDue: amountDueInPayment,
			amountDueBase,
			hasPriorPayment: priorPayment.hasPriorPayment,
			received,
			pending,
			pendingBase,
			change,
			changeBase,
			status,
			statusLabel,
			paidAmount: Math.min(received, amountDueInPayment),
			paymentCurrency,
			baseCurrency: billingBaseCurrency,
			showBaseCurrency: paymentCurrency !== billingBaseCurrency,
		};
	}, [
		billingBaseCurrency,
		billingSummary.grand_total,
		convertCurrency,
		payment.amount_received,
		payment.currency,
		priorPayment.balanceDueBase,
		priorPayment.hasPriorPayment,
		priorPayment.paidSoFarBase,
	]);

	const validateBilling = useCallback(({ allowZeroAmount = false } = {}) => {
		const validationErrors = {};
		if (!payment.currency) validationErrors.currency = t("Currency is required");
		if (!payment.payment_method) {
			validationErrors.payment_method = t("Payment method is required");
		}

		const rawAmount = payment.amount_received;
		const hasAmount =
			rawAmount !== "" && rawAmount !== null && rawAmount !== undefined;
		const amountDueLimit = paymentBalance.amountDue;

		if (!hasAmount && !allowZeroAmount) {
			validationErrors.amount_received = t("Amount is required");
		} else if (hasAmount) {
			const amount = Number(rawAmount);
			if (Number.isNaN(amount)) {
				validationErrors.amount_received = t("Amount is required");
			} else if (!allowZeroAmount && amount <= 0) {
				validationErrors.amount_received = t("Amount must be greater than zero");
			} else if (allowZeroAmount && amount < 0) {
				validationErrors.amount_received = t("Amount cannot be negative");
			} else if (amount > amountDueLimit + PAYMENT_TOLERANCE) {
				validationErrors.amount_received = t("Amount cannot exceed pending balance.");
			}
		}

		setErrors(validationErrors);
		return Object.keys(validationErrors).length === 0;
	}, [payment, paymentBalance.amountDue, t]);

	const buildBillingPayload = useCallback(
		({ markAsPaid = false } = {}) => {
			const summary = billingSummary;
			const receivedBase = convertBillingAmount(
				Number(payment.amount_received) || 0,
				payment.currency || billingBaseCurrency,
				billingBaseCurrency,
				convertCurrency
			);
			const cumulativePaidBase = markAsPaid
				? paymentBalance.grandTotalBase
				: paymentBalance.paidSoFarBase + receivedBase;
			const paymentStatus = resolvePaymentStatus(
				cumulativePaidBase,
				paymentBalance.grandTotalBase,
				markAsPaid
			);
			const grandTotalBase = paymentBalance.grandTotalBase;
			const settledPendingBase = markAsPaid ? 0 : paymentBalance.pendingBase;
			const settledChangeBase = markAsPaid ? 0 : paymentBalance.changeBase;

			const payloadCurrency = billingBaseCurrency || defaultCurrency;
			const toPayloadAmount = (amount, currency) =>
				normalizeBillingLineAmount({
					amount,
					currency,
					baseCurrency: payloadCurrency,
					convertCurrency,
					pharmacyCurrency: defaultCurrency,
					allowedCurrencies,
				});

			const servicesLineFields = buildSectionLineBillingFieldsMap({
				items: serviceItems,
				discountType: servicesDiscountType,
				discountValue: servicesDiscountValue,
				taxConfig: getEffectiveSectionTaxConfig(
					servicesTaxConfig,
					billingFlags.showServicesTax,
					posTaxType
				),
				isSameState,
				posTaxType,
				targetCurrency: payloadCurrency,
				convertCurrency,
				pharmacyCurrency: defaultCurrency,
				allowedCurrencies,
			});

			const showLabSection = isTreatmentPackage || billingFlags.showLabSection;

			const payloadInvestigationItems = showLabSection
				? filterNewBillingInvestigationsForPayload(investigationItems)
				: [];
			const payloadGeneralServiceItems =
				filterNewBillingGeneralServicesForPayload(serviceItems);

			const investigationsLineFields = buildSectionLineBillingFieldsMap({
				items: payloadInvestigationItems,
				discountType: investigationsDiscountType,
				discountValue: investigationsDiscountValue,
				taxConfig: getEffectiveSectionTaxConfig(
					investigationsTaxConfig,
					billingFlags.showLabTax,
					posTaxType
				),
				isSameState,
				posTaxType,
				targetCurrency: payloadCurrency,
				convertCurrency,
				pharmacyCurrency: defaultCurrency,
				allowedCurrencies,
			});

			const pharmacyItemBillingFields = buildPharmacyItemBillingFieldsMap({
				cart,
				itemTaxes,
				discountType,
				discountValue,
				totals,
				posTaxType,
				isSameState,
				targetCurrency: payloadCurrency,
				convertCurrency,
				pharmacyCurrency: defaultCurrency,
				allowedCurrencies,
			});

			const consultation = serviceItems
				.filter((item) => item.isConsultation)
				.map((item) => {
					const unitPrice = convertLineAmount(
						getServiceUnitPrice(item),
						item.currency,
						payloadCurrency,
						convertCurrency,
						defaultCurrency,
						allowedCurrencies
					);
					const { amount, currency } = toPayloadAmount(item.amount, item.currency);
					return {
						consultation_name: item.service_name,
						doctor: activePrescription?.doctor?.name || "",
						quantity: Math.max(1, Number(item.quantity) || 1),
						price: unitPrice,
						amount,
						currency,
						...(servicesLineFields.get(item.id) || {}),
					};
				});

			const general_services = payloadGeneralServiceItems.map((item) => {
				const unitPrice = convertLineAmount(
					getServiceUnitPrice(item),
					item.currency,
					payloadCurrency,
					convertCurrency,
					defaultCurrency,
					allowedCurrencies
				);
				const { amount, currency } = toPayloadAmount(item.amount, item.currency);
				return {
					service_id: item.service_id,
					service_name: item.service_name,
					quantity: Math.max(1, Number(item.quantity) || 1),
					price: unitPrice,
					amount,
					currency,
					source: item.source,
					...(servicesLineFields.get(item.id) || {}),
				};
			});

			const basePayload = {
				...buildBillingContextFields({
					prescription: activePrescription,
					selectedPatient,
					posTaxType,
				}),
				consultation,
				investigations: payloadInvestigationItems.map((item) => {
					const unitPrice = convertLineAmount(
						getInvestigationUnitPrice(item),
						item.currency,
						payloadCurrency,
						convertCurrency,
						defaultCurrency,
						allowedCurrencies
					);
					const { amount, currency } = toPayloadAmount(item.amount, item.currency);
					return {
						test_id: item.test_id,
						test_name: item.test_name,
						type: item.type,
						source: item.source,
						quantity: Math.max(1, Number(item.quantity) || 1),
						price: unitPrice,
						amount,
						currency,
						...(investigationsLineFields.get(item.id) || {}),
					};
				}),
				general_services,
				pharmacy_items: filterNewBillingPharmacyItemsForPayload(pharmacyItems).map(
					(item) => {
						const itemKey = `${item.item_id}-${item.stock_id}`;
						const { amount, currency } = toPayloadAmount(
							item.amount,
							defaultCurrency
						);
						const unitPrice = convertLineAmount(
							item.unit_price,
							defaultCurrency,
							payloadCurrency,
							convertCurrency,
							defaultCurrency,
							allowedCurrencies
						);
						return {
							...item,
							unit_price: unitPrice,
							amount,
							currency,
							date: item.date || formatPrescriptionDateForPayload(new Date()),
							...(pharmacyItemBillingFields.get(itemKey) || {}),
						};
					}
				),
				summary: {
					...summary,
					currency: payloadCurrency,
				},
				payment: {
					currency: payment.currency,
					payment_method: payment.payment_method,
					amount_received: Number(payment.amount_received) || 0,
					paid_amount: paymentBalance.paidAmount,
					prior_paid_amount: paymentBalance.paidSoFarBase,
					pending_amount: settledPendingBase,
					change_amount: settledChangeBase,
					status: paymentStatus,
					settled_as_paid: markAsPaid,
					base_currency: billingBaseCurrency,
					grand_total: grandTotalBase,
					grand_total_in_payment_currency: grandTotalBase,
					pending_amount_in_base_currency: settledPendingBase,
					change_amount_in_base_currency: settledChangeBase,
				},
				prescription_id: activePrescription?._id || null,
				appointment_invoice: activePrescription?.appointment_invoice || null,
			};

			const currentPaidAmount = toMoneyNumber(Number(payment.amount_received) || 0);
			const existingPaidInPaymentCurrency = toMoneyNumber(
				paymentBalance.paidSoFar ?? 0
			);
			const applyPaymentAmountFields = (payload) =>
				withBillingPaymentAmountFields(payload, {
					existingPaidAmount: existingPaidInPaymentCurrency,
					currentPaidAmount,
				});

			if (!isTreatmentPackage) return applyPaymentAmountFields(basePayload);

			const treatmentExtras = buildTreatmentBillingPayloadExtras({
				prescription: activePrescription,
				serviceItems,
				pharmacyItems,
				summary: basePayload.summary,
				paymentInfo: basePayload.payment,
			});

			return applyPaymentAmountFields({
				...basePayload,
				...treatmentExtras,
				consultation: basePayload.consultation,
				general_services: basePayload.general_services,
				investigations: basePayload.investigations,
				pharmacy_items: basePayload.pharmacy_items,
				summary: treatmentExtras.summary,
				payment: basePayload.payment,
				prescription_id: activePrescription?._id || null,
			});
		},
	[
		activePrescription,
		allowedCurrencies,
		cart,
		convertCurrency,
		defaultCurrency,
		discountType,
		discountValue,
		investigationItems,
		investigationsDiscountType,
		investigationsDiscountValue,
		investigationsTaxConfig,
		isSameState,
		itemTaxes,
		patientStateCode,
		pharmacyStateCode,
		serviceItems,
		totals,
		payment,
		paymentBalance,
		pharmacyItems,
		posTaxType,
		selectedPatient,
		servicesDiscountType,
		servicesDiscountValue,
		servicesTaxConfig,
		billingBaseCurrency,
		billingSummary,
		isTreatmentPackage,
	]);

	const buildFinalSalePayload = useCallback(
		({ markAsPaid = false, optionalSaleMeta = {} } = {}) => {
			const billingPayload = buildBillingPayload({ markAsPaid });
			const billingPayment = billingPayload.payment || {};
			const saleFlags = resolveBillingSalePaymentFlags({
				markAsPaid,
				status: billingPayment.status,
				settledAsPaid: billingPayment.settled_as_paid,
			});

			// Top-level POS sale = pharmacy cart only (package totals stay in billing)
			const pharmacySourceCurrency = currency || defaultCurrency;
			const pharmacyNetTotal = convertBillingAmount(
				totals?.netTotal || 0,
				pharmacySourceCurrency,
				payment.currency,
				convertCurrency
			);
			const received = Number(payment.amount_received) || 0;
			const pharmacyPaidAmount = toMoneyNumber(
				Math.min(received, pharmacyNetTotal)
			);
			const saleTotalPaid =
				saleFlags.paidType === "full" ? pharmacyNetTotal : pharmacyPaidAmount;

			const paymentData = buildSalePaymentData({
				currency: payment.currency,
				type: payment.payment_method,
				amount: saleTotalPaid,
				paidType: saleFlags.paidType,
			});

			const salePayload = buildInvoicePayloadWithTax({
				cart,
				itemTaxes,
				discountType,
				discountValue,
				transportCost,
				paymentData,
				paidType: saleFlags.paidType,
				customerType,
				selectedPatient,
				pharmacyPOS,
				posTaxType,
				pharmacyStateCode,
				patientStateCode,
				convertCurrency,
				sourceCurrency: currency,
				...optionalSaleMeta,
				t,
			});

			return {
				...salePayload,
				netTotal: pharmacyNetTotal,
				totalPaid: saleTotalPaid,
				isPartialPaid: saleFlags.isPartialPaid,
				isPaid: saleFlags.isPaid,
				isSettled: saleFlags.isSettled,
				billing: billingPayload,
			};
		},
		[
			buildBillingPayload,
			buildSalePaymentData,
			cart,
			convertCurrency,
			currency,
			customerType,
			defaultCurrency,
			discountType,
			discountValue,
			itemTaxes,
			payment,
			pharmacyPOS,
			pharmacyStateCode,
			patientStateCode,
			posTaxType,
			selectedPatient,
			t,
			totals?.netTotal,
			transportCost,
		]
	);

	const handleBillingSubmit = useCallback(
		async ({ markAsPaid = false } = {}) => {
			setSubmitted(true);
			if (!validateBilling({ allowZeroAmount: markAsPaid })) return null;

			let resolvedDoctorId = doctorId;
			if (!resolvedDoctorId && sanitizeDoctorName(doctorForm?.name)) {
				try {
					resolvedDoctorId = await resolveDoctorIdForSale({
						doctorId,
						doctorForm,
					});
					if (resolvedDoctorId && typeof setDoctorId === "function") {
						setDoctorId(resolvedDoctorId);
					}
				} catch (error) {
					console.error("Error resolving doctor for billing sale:", error);
					toast.error(
						error?.response?.data?.message ||
							t("Could not link doctor to this sale. Please try again.")
					);
					return null;
				}
			}

			const prescriptionDate = formatPrescriptionDateForPayload(
				activePrescription?.createdAt
			);
			const optionalSaleMeta = buildOptionalSaleMeta({
				doctorId: resolvedDoctorId,
				doctorForm,
				prescriptionDate,
				includePrescriptionDate: Boolean(prescriptionDate),
			});

			const finalPayload = buildFinalSalePayload({ markAsPaid, optionalSaleMeta });
			const paymentDataForInvoice = buildSalePaymentData({
				currency: payment.currency,
				type: payment.payment_method,
				amount: Number(payment.amount_received) || 0,
				paidType:
					markAsPaid ||
					isFullyPaidAmount(
						paymentBalance.paidSoFarBase +
							convertBillingAmount(
								Number(payment.amount_received) || 0,
								payment.currency || billingBaseCurrency,
								billingBaseCurrency,
								convertCurrency
							),
						paymentBalance.grandTotalBase
					)
						? "full"
						: "partial",
			});

			setBillingSubmitting(true);
			setLoading?.(true, t("Payment processing..."));
			try {
				const formData = buildSalesFormData(finalPayload, prescriptionFile);
				const response = await addSales(formData);
				const salesData = response?.data?.data?.sales;
				const responseInvoiceUrl = salesData?.invoiceUrl;

				toast.success(response.data?.message || t("Sale saved successfully"));

				// Close billing first so it cannot block / steal focus from the invoice modal.
				closeBillingModal();

				const openInvoicePreview = () => {
					try {
						if (pharmacyCollectsPayment && responseInvoiceUrl) {
							setBackendInvoiceUrl(responseInvoiceUrl);
							setBackendInvoiceNo(salesData?.salesNumber || "");
							setShowBackendInvoice(true);
							return;
						}

						const invoice = buildBillingInvoiceData({
							response,
							markAsPaid,
							activePrescription,
							selectedPatient,
							cart,
							itemTaxes,
							serviceItems,
							investigationItems,
							billingSummary,
							payment,
							paymentBalance,
							totals,
							convertCurrency,
							sourceCurrency: currency,
							getCurrencySymbol,
							pharmacyPOS,
							posTaxType,
							pharmacyStateCode,
							patientStateCode,
							discountType,
							discountValue,
							transportCost,
							patientDisplayName: paymentDataForInvoice.patientName,
							servicesTaxConfig,
							investigationsTaxConfig,
						});

						setBillingInvoiceData(invoice);
						setShowBillingInvoice(true);
					} catch (invoiceError) {
						console.error("Error preparing invoice preview:", invoiceError);
						toast.warning(
							t("Sale saved, but invoice preview could not be opened.")
						);
					}
				};

				// Defer so the billing modal can finish hiding before the invoice opens.
				window.setTimeout(openInvoicePreview, 300);

				return response;
			} catch (error) {
				console.error("Error saving billing sale:", error);
				// toast.error(
				// 	error.response?.data?.message ||
				// 		t("Failed to save sale. Please try again later.")
				// );
				return null;
			} finally {
				setBillingSubmitting(false);
				setLoading?.(false);
			}
		},
		[
			activePrescription,
			billingSummary,
			buildFinalSalePayload,
			buildSalePaymentData,
			cart,
			closeBillingModal,
			convertCurrency,
			currency,
			discountType,
			discountValue,
			doctorForm,
			doctorId,
			getCurrencySymbol,
			investigationItems,
			itemTaxes,
			payment,
			paymentBalance,
			prescriptionFile,
			selectedPatient,
			serviceItems,
			servicesTaxConfig,
			investigationsTaxConfig,
			setDoctorId,
			setLoading,
			t,
			totals,
			transportCost,
			validateBilling,
			posTaxType,
			pharmacyPOS,
			pharmacyStateCode,
			patientStateCode,
			pharmacyCollectsPayment,
		]
	);

	return {
		showBillingModal,
		billingLoading,
		billingSubmitting,
		showBillingInvoice,
		billingInvoiceData,
		closeBillingInvoice,
		showBackendInvoice,
		backendInvoiceUrl,
		backendInvoiceNo,
		closeBackendInvoice,
		openBillingModal,
		closeBillingModal,
		resetBillingState,
		activePrescription,
		serviceItems,
		servicesBillingTotals,
		servicesDiscountType,
		servicesDiscountValue,
		servicesTaxConfig,
		investigationsBillingTotals,
		investigationsDiscountType,
		investigationsDiscountValue,
		investigationsTaxConfig,
		investigationItems,
		suggestedInvestigations,
		generalMasterList,
		labMasterList,
		pharmacyItems,
		priorPharmacyItems,
		billingSummary,
		billingBaseCurrency,
		defaultCurrency,
		payment,
		updatePayment,
		errors,
		submitted,
		sectionOpen,
		toggleSection,
		updateInvestigationAmount,
		updateInvestigationQuantity,
		updateServiceAmount,
		updateServiceQuantity,
		updateServicesDiscount,
		updateServicesTaxConfig,
		updateInvestigationsDiscount,
		updateInvestigationsTaxConfig,
		removeInvestigation,
		removeServiceItem,
		addServiceRow,
		addInvestigationRow,
		selectServiceForRow,
		selectInvestigationForRow,
		paymentBalance,
		handleBillingSubmit,
		isTreatmentPackage,
		treatmentDetails,
		treatmentPaymentHistory,
		packagePrice,
	};
};
